import {
  Button,
  DeleteButton,
  EditButton,
  FilterDropdown,
  Form,
  Icons,
  Input,
  List,
  Radio,
  Space,
  Tag,
  Typography,
  useModalForm,
  useTable,
} from "@pankod/refine-antd";
import { Table } from "@pankod/refine-antd";
import {
  CrudFilters,
  HttpError,
  IResourceComponentsProps,
} from "@pankod/refine-core";
import dayjs from "dayjs";
import { SuperCategory } from "interfaces/supercategory";
import { CreateSuperCategory } from "./create";
import { EditSuperCategory } from "./edit";

const { Title } = Typography;

interface SearchVariables {
  q: string;
}

export const SuperCategoryList: React.FC<IResourceComponentsProps> = ({
  name,
}) => {
  const [searchForm] = Form.useForm();
  const { tableProps, searchFormProps } = useTable<
    SuperCategory.SuperCategoryItem,
    HttpError,
    SearchVariables
  >({
    resource: name,
    initialSorter: [
      {
        field: "order",
        order: "asc",
      },
    ],
    onSearch: (params) => {
      const { q } = params;

      return [
        {
          field: "search",
          operator: "contains",
          value: q || undefined,
        },
      ];
    },
  });

  const {
    formProps: createFormProps,
    modalProps: createModalProps,
    show: showCreateModal,
  } = useModalForm({
    resource: name,
    action: "create",
    submitOnEnter: true,
    redirect: false,
    successNotification: {
      type: "success",
      message: "Süper kategori başarıyla oluşturuldu.",
    },
    onMutationSuccess: () => {
      createFormProps.form.resetFields();
    },
  });

  const {
    formProps: editFormProps,
    modalProps: editModalProps,
    show: showEditModal,
  } = useModalForm({
    resource: name,
    action: "edit",
    submitOnEnter: true,
    redirect: false,
    successNotification: {
      type: "success",
      message: "Süper kategori başarıyla güncellendi.",
    },
  });

  return (
    <List
      resource={name}
      title="Süper Kategoriler"
      createButtonProps={{
        type: "primary",
        size: "large",
        shape: "round",
        icon: <Icons.PlusCircleFilled />,
        children: "Yeni Süper Kategori Oluştur",
        onClick: () => showCreateModal(),
      }}
    >
      <Form {...searchFormProps} form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item name="q">
          <Input
            placeholder="İsim veya koda göre arama yapınız..."
            prefix={<Icons.SearchOutlined />}
            allowClear
            style={{ width: 300 }}
          />
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" type="primary">
            Ara
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            onClick={() => {
              searchForm.resetFields();
              searchForm.submit();
            }}
          >
            Sıfırla
          </Button>
        </Form.Item>
      </Form>

      <Table {...tableProps}>
        <Table.Column
          dataIndex="name"
          title="İsim"
          key="name"
          sorter
          render={(value) => <span>{value}</span>}
        />
        <Table.Column
          dataIndex="code"
          title="Kod"
          key="code"
          sorter
          render={(value) => <span>{value || "-"}</span>}
        />
        <Table.Column<SuperCategory.SuperCategoryItem>
          dataIndex="isVisible"
          title="Durum"
          key="isVisible"
          render={(_, record) => {
            if (record.isVisible) {
              return <Tag color="success">Aktif</Tag>;
            }
            return <Tag color="red">Pasif</Tag>;
          }}
          filterDropdown={(props) => (
            <FilterDropdown {...props}>
              <Title level={5}>Durum</Title>
              <Radio.Group
                options={[
                  { label: "Aktif", value: "1" },
                  { label: "Pasif", value: "0" },
                ]}
              />
            </FilterDropdown>
          )}
        />
        <Table.Column
          dataIndex="order"
          title="Gösterim Sırası"
          key="order"
          sorter
          render={(value) => <span>{value ?? "-"}</span>}
        />
        <Table.Column
          dataIndex={["subCatalog", "name"]}
          title="Alt Katalog"
          key="subCatalog"
          render={(value) => <span>{value || "-"}</span>}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Oluşturulma Tarihi"
          key="createdAt"
          sorter
          render={(value) => (
            <span>{dayjs(value).format("DD/MM/YYYY HH:mm")}</span>
          )}
        />
        <Table.Column
          dataIndex="updatedAt"
          title="Güncellenme Tarihi"
          key="updatedAt"
          sorter
          render={(value) => (
            <span>{dayjs(value).format("DD/MM/YYYY HH:mm")}</span>
          )}
        />
        <Table.Column<SuperCategory.SuperCategoryItem>
          title="İşlemler"
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              <EditButton
                size="small"
                onClick={() => {
                  showEditModal(record.id);
                }}
              >
                Düzenle
              </EditButton>
              <DeleteButton
                resourceNameOrRouteName={name}
                size="small"
                recordItemId={record.id}
                successNotification={{
                  type: "success",
                  message: "Süper kategori başarıyla silindi.",
                }}
              >
                Sil
              </DeleteButton>
            </Space>
          )}
        />
      </Table>
      <CreateSuperCategory
        formProps={createFormProps}
        modalProps={createModalProps}
      />
      <EditSuperCategory
        formProps={editFormProps}
        modalProps={editModalProps}
      />
    </List>
  );
};
